import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { notify } from "@/components/ui/dialogs";
import { post, completeSignIn, logout } from "../../utils/service";
import AuthShell from "@/components/auth/AuthShell";

export type AuthNotice = { title: string; message: string; tone: "info" | "error" };

const PROVIDER = (p?: string | null) => (p === "microsoft" ? "Microsoft" : "Google");

/** Plain-language explanations for every way an OAuth sign-in can end without signing in. */
export function describeOAuthError(code: string, message: string | null, provider?: string | null): AuthNotice {
  const name = PROVIDER(provider);
  switch (code) {
    case "oauth_not_connected":
      return {
        tone: "info",
        title: `${name} sign-in isn't turned on for your account yet`,
        message: message ?? `Sign in with your email and password first, then open Settings → Sign-in Methods and connect your ${name} account. After that you can use either option.`,
      };
    case "no_account":
      return { tone: "info", title: "We couldn't find your account", message: message ?? "Accounts are created by invitation. Ask your organization administrator to invite you." };
    case "cancelled":
      return { tone: "info", title: "Sign-in was cancelled", message: "Nothing was changed. You can try again or sign in with your email and password." };
    case "provider_disabled":
      return { tone: "error", title: "This sign-in option isn't available", message: "Please sign in with your email and password." };
    case "oauth_failed":
      return { tone: "error", title: `We couldn't complete sign-in with ${name}`, message: "Please try again in a moment, or sign in with your email and password." };
    default:
      return { tone: "error", title: "Sign-in didn't go through", message: message ?? "Please try again, or sign in with your email and password." };
  }
}

/**
 * Where Google / Microsoft send the browser back to (via the API). Exchanges the one-time code
 * for a session, finishes connecting a sign-in method, or explains why sign-in did not happen.
 */
export default function OAuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const code = params.get("code");
    const linked = params.get("linked");
    const error = params.get("error");
    const intent = params.get("intent");

    if (linked) {
      navigate(`/settings?tab=signin&linked=${encodeURIComponent(linked)}`, { replace: true });
      return;
    }

    if (error) {
      const notice = describeOAuthError(error, params.get("message"), params.get("provider"));
      if (intent === "link") {
        notify(notice.message, { title: notice.title, tone: "error" });
        navigate("/settings?tab=signin", { replace: true });
      } else {
        navigate("/login", { replace: true, state: { notice } });
      }
      return;
    }

    if (!code) {
      navigate("/login", { replace: true });
      return;
    }

    post<{ data: { token: string; refresh_token: string } }>("/auth/oauth/exchange", { code })
      .then(async (r) => {
        const { hasOrganization, platformAdmin } = await completeSignIn(r.data, true);
        if (platformAdmin) {
          navigate("/platform", { replace: true });
          return;
        }
        if (!hasOrganization) {
          logout();
          return;
        }
        navigate("/", { replace: true });
      })
      .catch((e) => {
        navigate("/login", {
          replace: true,
          state: { notice: describeOAuthError("expired", e?.response?.data?.message ?? "This sign-in link has expired. Please try again.") },
        });
      });
  }, [params, navigate]);

  return (
    <AuthShell title="Signing you in" subtitle="One moment…">
      <div className="h-1 w-full overflow-hidden rounded-full bg-gray-100">
        <div className="h-full w-1/3 animate-pulse rounded-full bg-[#ff6d27]" />
      </div>
    </AuthShell>
  );
}
